
import React, { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import { X, Minus, ChevronRight } from 'lucide-react';
import { fetchLeagueStandings, fetchTopScorers, fetchTopAssists, fetchLeagueFixtures, fetchLeagues, LeagueDB, fetchLeagueInjuries, fetchUserLeagueSelectionsFromDB, fetchLeaguesByIds, getTeamLogo, getLeagueLogo } from '../../services/footballService';
import { StandingItem, Game } from '../../types';
import { ImageWithFallback } from '../Common/ImageWithFallback';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../i18n';
import { getLeagueName, getLeaguePolicy } from '../../services/leagueService';
import { getTeamName } from '../../services/teamService';
import { BracketView } from './BracketView';
import { WorldCupScenarios } from './WorldCupScenarios';

interface StandingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    leagueId: string;
    leagueName: string;
    leagueLogo: string;
    season: string;
    onLeagueChange?: (leagueId: string, leagueName: string, leagueLogo: string) => void;
    allowLeagueChange?: boolean;
    homeTeamId?: string;
    awayTeamId?: string;
    initialSubTab?: 'league' | 'knockout' | 'scenario';
    initialKnockoutRound?: 'r32' | 'r16' | 'qf' | 'sf' | 'final';
}

type TabType = 'table' | 'scorers' | 'assists' | 'injuries';

// Sub-components for better performance with React.memo
const StandingRow = memo(({ item, index, lang, leagueId, shouldShowCupQualification, getStatusColor, getTeamName }: any) => {
    const policy = getLeaguePolicy(parseInt(leagueId));
    const statusColor = (shouldShowCupQualification || (policy?.rel.includes(item.rank) || policy?.rel_po.includes(item.rank)))
        ? getStatusColor(item.rank)
        : 'bg-transparent';

    let rankColor = 'text-slate-400';
    if (policy) {
        if (shouldShowCupQualification) {
            if (policy.ucl.includes(item.rank)) rankColor = 'text-blue-600';
            else if (policy.uel.includes(item.rank)) rankColor = 'text-orange-500';
            else if (policy.uecl.includes(item.rank)) rankColor = 'text-emerald-500';
        }
        if (policy.rel_po.includes(item.rank)) rankColor = 'text-amber-600';
        else if (policy.rel.includes(item.rank)) rankColor = 'text-red-500';
    }

    return (
        <div className={`group relative grid grid-cols-12 gap-1 items-center py-4 px-4 border-b border-gray-50 md:hover:bg-rose-50/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'}`}>
            <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${statusColor} transition-all`}></div>
            <div className={`col-span-1 font-black text-xs text-center ${rankColor}`}>{item.rank}</div>
            <div className="col-span-5 flex items-center gap-2.5 text-left overflow-hidden">
                <div className="w-7 h-7 p-1 bg-white rounded-lg border border-gray-100 shadow-sm shrink-0 flex items-center justify-center">
                    <ImageWithFallback src={getTeamLogo(item.team?.id) || item.team?.logo} alt="" className="w-full h-full object-contain" />
                </div>
                <span className="text-xs font-bold text-slate-800 truncate leading-none">{getTeamName(item.team?.id, lang, item.team?.name)}</span>
            </div>
            <div className="col-span-1 text-[11px] font-medium text-slate-400 text-center">{item.played}</div>
            <div className="col-span-1 text-center">
                <span className="text-[11px] font-black text-slate-900 bg-gray-100 px-1.5 py-0.5 rounded-md md:group-hover:bg-white md:group-hover:shadow-sm border border-transparent md:group-hover:border-rose-100 transition-all">
                    {item.points}
                </span>
            </div>
            <div className={`col-span-1 text-[10px] font-black text-center ${item.goalDifference > 0 ? 'text-emerald-500' : (item.goalDifference < 0 ? 'text-rose-500' : 'text-gray-300')}`}>
                {item.goalDifference > 0 ? `+${item.goalDifference}` : item.goalDifference}
            </div>
            <div className="col-span-1 text-[11px] font-bold text-slate-400 text-center">{item.won}</div>
            <div className="col-span-1 text-[11px] font-bold text-slate-400 text-center">{item.drawn}</div>
            <div className="col-span-1 text-[11px] font-bold text-slate-400 text-center">{item.lost}</div>
        </div>
    );
});

const StatItem = memo(({ item, index, activeTab, lang, handlePlayerClick, getTeamName }: any) => {
    return (
        <div
            onClick={() => handlePlayerClick(item)}
            className="group flex items-center gap-4 bg-white rounded-3xl p-3 border border-gray-100 md:hover:border-rose-100 md:hover:shadow-sm md:hover:shadow-rose-50 transition-all cursor-pointer active:scale-[0.98]"
        >
            <div className="w-14 h-14 rounded-2xl overflow-hidden bg-gray-50 border-2 border-white shadow-sm shrink-0 relative">
                <img src={item.player.photo} className="w-full h-full object-cover" alt="" />
                <div className="absolute top-0.5 left-0.5 bg-slate-900 text-white text-[8px] font-black px-1 rounded-md shadow-sm">#{index + 1}</div>
            </div>
            <div className="flex-1 min-w-0">
                <h4 className="text-sm font-black text-slate-900 leading-none truncate md:group-hover:text-rose-600 transition-colors">{item.player.name}</h4>
                <div className="flex items-center gap-2 mt-1.5">
                    <div className="w-4 h-4 shrink-0"><ImageWithFallback src={getTeamLogo(item.statistics[0].team.id) || item.statistics[0].team.logo} className="w-full h-full object-contain" alt="" /></div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight truncate">{getTeamName(item.statistics[0].team.id, lang, item.statistics[0].team.name)}</span>
                </div>
            </div>
            <div className="text-right flex items-center gap-3">
                <div className="flex flex-col items-end">
                    <span className="text-xl font-black text-slate-900 leading-none tabular-nums">
                        {activeTab === 'scorers' ? item.statistics[0].goals.total : item.statistics[0].goals.assists || 0}
                    </span>
                    <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest mt-0.5">
                        {activeTab === 'scorers' ? t(lang, 'goals') : t(lang, 'assistsFor')}
                    </span>
                </div>
                <ChevronRight size={16} className="text-gray-200 md:group-hover:text-rose-400 transition-all" />
            </div>
        </div>
    );
});

const InjuryItem = memo(({ item, lang, handlePlayerClick }: any) => {
    return (
        <div
            onClick={() => handlePlayerClick(item)}
            className="group flex items-center gap-4 bg-white rounded-3xl p-3 border border-gray-100 md:hover:border-rose-100 md:hover:shadow-sm md:hover:shadow-rose-50 transition-all cursor-pointer active:scale-[0.98]"
        >
            <div className="w-14 h-14 relative ml-5">
                <img src={item.player.photo} className="w-full h-full object-cover rounded-2xl" alt="" />
                <div className="absolute -top-1 -left-6 w-8 h-8 shrink-0"><ImageWithFallback src={getTeamLogo(item.team.id) || item.team.logo} className="w-full h-full object-contain" alt="" /></div>
            </div>
            <div className="flex-1 min-w-0">
                <h4 className="text-sm font-black text-slate-900 leading-none truncate md:group-hover:text-rose-600 transition-colors">{item.player.name}</h4>
                <div className="mt-1">
                    <span className="text-[12px] text-gray-400 font-medium">
                        {item.player.reason
                            ? t(lang, `injuryReason_${item.player.reason.replace(/\s+/g, '')}`) !== `injuryReason_${item.player.reason.replace(/\s+/g, '')}`
                                ? t(lang, `injuryReason_${item.player.reason.replace(/\s+/g, '')}`)
                                : item.player.reason
                            : ''}
                    </span>
                </div>
            </div>
            <ChevronRight size={16} className="text-gray-200 md:group-hover:text-rose-400 transition-all" />
        </div>
    );
});

export const StandingsModal: React.FC<StandingsModalProps> = ({ isOpen, onClose, leagueId, leagueName, leagueLogo, season, onLeagueChange, allowLeagueChange = true, homeTeamId, awayTeamId, initialSubTab, initialKnockoutRound }) => {
    const { lang } = useLanguage();
    const { user } = useAuth();
    const [standings, setStandings] = useState<StandingItem[]>([]);
    const [topScorers, setTopScorers] = useState<any[]>([]);
    const [topAssists, setTopAssists] = useState<any[]>([]);
    const [injuries, setInjuries] = useState<any[]>([]);
    const [selectedInjuryTeamId, setSelectedInjuryTeamId] = useState<number | null>(null);
    const injuryListRef = useRef<HTMLDivElement>(null);
    
    const [loading, setLoading] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [activeTab, setActiveTab] = useState<TabType>('table');
    const [subTab, setSubTab] = useState<'league' | 'knockout' | 'scenario'>('league');
    const [fixtures, setFixtures] = useState<Game[]>([]);
    const [availableLeagues, setAvailableLeagues] = useState<LeagueDB[]>([]);
    const [isLeagueSelectorOpen, setIsLeagueSelectorOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

    // 월드컵(id=1)은 캘린더 시즌이므로 항상 2026 사용
    const effectiveSeason = useMemo(() => {
        if (leagueId === '1') return '2026';
        return season;
    }, [leagueId, season]);

    const isUclUel = useMemo(() => {
        const lowerLeagueName = leagueName.toLowerCase();
        // 45: FA Cup, 48: EFL Cup
        const cupIds = ['1', '2', '3', '45', '48', '81', '143', '137', '66', '17', '16', '15', '10', '4', '531', '848'];
        const isCup = (
            lowerLeagueName.includes('champions league') || 
            lowerLeagueName.includes('europa league') || 
            lowerLeagueName.includes('cup') || 
            lowerLeagueName.includes('pokal') || 
            lowerLeagueName.includes('copa') || 
            lowerLeagueName.includes('coppa') || 
            lowerLeagueName.includes('coupe') || 
            cupIds.includes(leagueId)
        ) && leagueId !== '140' && leagueId !== '39' && leagueId !== '78' && leagueId !== '135' && leagueId !== '61';

        return isCup;
    }, [leagueName, leagueId]);

    const showBracketOnly = useMemo(() => {
        const cupIds = ['45', '48']; // FA Cup, EFL Cup
        return cupIds.includes(leagueId);
    }, [leagueId]);

    const groupNames = useMemo(() => {
        if (leagueId !== '1') return [];
        return Array.from(new Set(standings.filter(s => s.group).map(s => s.group!))).sort();
    }, [standings, leagueId]);

    // 컵 진출 정보(UCL, UEL 등)를 표시해야 하는 리그인지 확인
    const shouldShowCupQualification = useMemo(() => {
        // 이미 컵 대회인 경우(UCL, UEL, 월드컵 등)는 제외
        if (isUclUel) return false;
        
        // 리그 정책이 있고, 컵 진출권(ucl, uel, uecl) 중 하나라도 설정되어 있는 경우만 표시
        const policy = getLeaguePolicy(parseInt(leagueId));
        if (policy) {
            return policy.ucl.length > 0 || policy.uel.length > 0 || policy.uecl.length > 0;
        }
        
        return false;
    }, [isUclUel, leagueId]);

    useEffect(() => {
        if (isOpen) {
            loadLeagues();
            setSelectedGroup(null);
            setSubTab(initialSubTab || 'league');
            const timer = setTimeout(() => setIsAnimating(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsAnimating(false);
            setIsLeagueSelectorOpen(false);
        }
    }, [isOpen, leagueId, season]);

    useEffect(() => {
        if (isOpen) {
            loadData(activeTab);
        }
    }, [isOpen, leagueId, season, activeTab]);

    const loadLeagues = async () => {
        try {
            let selections: { league_id: string, order: number }[] = [];
            
            if (user) {
                // 로그인한 경우 DB에서 가져옴
                selections = await fetchUserLeagueSelectionsFromDB(user.id);
            } else {
                // 로그인하지 않은 경우 로컬 스토리지 세션 확인
                const sessionStr = localStorage.getItem('xleven_league_session');
                if (sessionStr) {
                    const session = JSON.parse(sessionStr);
                    // GamesView와 동일한 세션 만료 로직 (3분) 적용 여부는 선택사항이나, 
                    // 여기서는 저장된 값이 있으면 우선 사용하도록 함
                    selections = session.selections || [];
                }
            }

            if (selections.length > 0) {
                const selectedLeagueIds = selections.map(s => s.league_id);
                const selectedLeagues = await fetchLeaguesByIds(selectedLeagueIds);
                
                if (selectedLeagues.length > 0) {
                    // 순서 보장 (order 기준)
                    const sortedSelected = selectedLeagues.sort((a, b) => {
                        const orderA = selections.find(s => s.league_id === a.id)?.order || 0;
                        const orderB = selections.find(s => s.league_id === b.id)?.order || 0;
                        return orderA - orderB;
                    });
                    setAvailableLeagues(sortedSelected);
                    return;
                }
            }

            // 선택한 리그가 없는 경우 기본 주요 리그 표시
            const leagues = await fetchLeagues();
            setAvailableLeagues(leagues);
        } catch (error) {
            console.error('Failed to fetch leagues for modal:', error);
        }
    };

    const loadData = async (tab: TabType) => {
        // 이미 데이터가 있는 경우 로딩 상태 없이 조용히 업데이트하거나 유도
        const hasData = (tab === 'table' && standings.length > 0) ||
                       (tab === 'scorers' && topScorers.length > 0) ||
                       (tab === 'assists' && topAssists.length > 0) ||
                       (tab === 'injuries' && injuries.length > 0);
        
        if (!hasData) {
            setLoading(true);
        } else {
            // Restore scroll position when data already exists
            requestAnimationFrame(() => {
                const scrollPos = sessionStorage.getItem(`standings_scroll_${leagueId}_${tab}`);
                if (scrollPos && injuryListRef.current) {
                    injuryListRef.current.scrollTop = parseInt(scrollPos);
                }
            });
        }
        
        try {
            if (tab === 'table') {
                const data = await fetchLeagueStandings(leagueId, effectiveSeason);
                setStandings(data);

                if (isUclUel) {
                    const fixtureData = await fetchLeagueFixtures(leagueId, effectiveSeason);
                    setFixtures(fixtureData);
                }
            } else if (tab === 'scorers') {
                const data = await fetchTopScorers(leagueId, effectiveSeason);
                setTopScorers(data);
            } else if (tab === 'assists') {
                const data = await fetchTopAssists(leagueId, effectiveSeason);
                setTopAssists(data);
            } else if (tab === 'injuries') {
                const data = await fetchLeagueInjuries(leagueId, effectiveSeason);
                // league ID가 명시적으로 다른 리그인 경우만 제외 (null/undefined면 포함)
                let filteredInjuries = data.filter((item: any) => {
                    const itemLeagueId = item.league?.id ?? item.fixture?.league?.id;
                    if (itemLeagueId == null) return true;
                    return itemLeagueId.toString() === leagueId.toString();
                });

                // 경기 상세에서 온 경우 홈/어웨이 팀만 필터링
                if (homeTeamId && awayTeamId) {
                    filteredInjuries = filteredInjuries.filter((item: any) => 
                        item.team?.id?.toString() === homeTeamId.toString() || 
                        item.team?.id?.toString() === awayTeamId.toString()
                    );
                }

                // 선수 ID 기준 중복 제거
                const uniqueInjuries = Array.from(new Map(filteredInjuries.map(item => [item.player.id, item])).values());
                setInjuries(uniqueInjuries);
            }

            // After loading data, restore scroll
            requestAnimationFrame(() => {
                const scrollPos = sessionStorage.getItem(`standings_scroll_${leagueId}_${tab}`);
                if (scrollPos && injuryListRef.current) {
                    injuryListRef.current.scrollTop = parseInt(scrollPos);
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = useCallback(() => {
        setIsAnimating(false);
        setTimeout(onClose, 500);
    }, [onClose]);

    const getStatusColor = useCallback((rank: number) => {
        // 컵 대회의 경우 상태 색상 표시 안함
        if (isUclUel) {
            return 'bg-transparent';
        }

        const policy = getLeaguePolicy(parseInt(leagueId));
        if (policy) {
            if (policy.ucl.includes(rank)) return 'bg-blue-500';
            if (policy.uel.includes(rank)) return 'bg-orange-400';
            if (policy.uecl.includes(rank)) return 'bg-emerald-400';
            if (policy.rel_po.includes(rank)) return 'bg-amber-500';
            if (policy.rel.includes(rank)) return 'bg-red-500';
            return 'bg-transparent';
        }

        // 기본값 (정책이 없는 경우)
        if (rank <= 4) return 'bg-blue-500'; 
        if (rank <= 6) return 'bg-orange-400'; 
        if (rank > (standings.length || 20) - 3) return 'bg-red-500'; 
        return 'bg-transparent';
    }, [leagueName, leagueId, standings.length]);

    const handlePlayerClick = useCallback((p: any) => {
        if (!p?.player?.id) return;
        const playerId = p.player.id.toString();
        const win = window as any;
        if (win.navigateTo) win.navigateTo(`/player/${playerId}`);
        handleClose();
    }, [handleClose]);

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 z-[300] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>

            <div className={`relative bg-white w-full h-[90vh] sm:max-w-xl sm:h-[85vh] rounded-t-[40px] sm:rounded-[40px] shadow-sm flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0 sm:scale-100' : 'translate-y-full sm:scale-95'}`}>
                
                <div className="w-full flex justify-center pt-4 pb-2 sm:hidden" onClick={handleClose}>
                    <div className="w-12 h-1.5 bg-gray-100 rounded-full"></div>
                </div>

                <div className="px-6 py-5 border-b border-gray-50 flex items-center justify-between bg-white z-20">
                    <button 
                        onClick={() => allowLeagueChange && setIsLeagueSelectorOpen(!isLeagueSelectorOpen)}
                        className={`flex items-center gap-4 text-left group min-w-0 ${!allowLeagueChange ? 'cursor-default' : ''}`}
                    >
                        <div className="relative shrink-0">
                            <div className="w-12 h-12 rounded-2xl bg-gray-50 p-2.5 border border-gray-100 flex items-center justify-center group-hover:bg-rose-50 transition-colors">
                                <ImageWithFallback 
                                    src={leagueId === '1' ? "/assets/fiwc.png" : (getLeagueLogo(leagueId) || leagueLogo)}
                                    alt="logo" 
                                    className="w-full h-full object-contain mix-blend-multiply" 
                                />
                            </div>
                            <div className="absolute -bottom-1 -right-1 bg-slate-900 text-white text-[8px] font-black px-1.5 py-0.5 rounded-lg border-2 border-white shadow-sm">
                                {leagueId === '1' ? '2026' : `${season.slice(-2)}/${(parseInt(season.slice(-2)) + 1)}`}
                            </div>
                        </div>
                        <div className="overflow-hidden">
                            <div className="flex items-center gap-2">
                                <h2 className={`text-lg md:text-xl font-black text-gray-900 leading-none tracking-tight truncate transition-colors ${allowLeagueChange ? 'group-hover:text-rose-500' : ''}`}>
                                    {getLeagueName(parseInt(leagueId), lang, leagueName)}
                                </h2>
                                {allowLeagueChange && <ChevronRight size={16} className={`text-gray-300 transition-transform duration-300 ${isLeagueSelectorOpen ? 'rotate-90' : ''}`} />}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-[9px] text-gray-400 font-black tracking-widest uppercase">{t(lang, 'activityStats')}</span>
                            </div>
                        </div>
                    </button>
                    <button onClick={handleClose} className="bg-gray-50 text-gray-400 md:hover:bg-gray-100 md:hover:text-gray-900 p-2.5 rounded-full transition-all active:scale-95">
                        <X size={20} strokeWidth={2.5} />
                    </button>
                </div>

                {isLeagueSelectorOpen && (
                    <div className="absolute top-[100px] md:top-[88px] left-0 right-0 bottom-0 bg-white z-[30] overflow-y-auto custom-scrollbar animate-in slide-in-from-top duration-300">
                        <div className="p-4 grid grid-cols-1 gap-2">
                            {availableLeagues.map(league => (
                                <button
                                    key={league.id}
                                    onClick={() => {
                                        if (onLeagueChange) {
                                            onLeagueChange(league.id, league.name, league.logo_url);
                                        }
                                        setIsLeagueSelectorOpen(false);
                                    }}
                                    className={`flex items-center gap-4 p-4 rounded-2xl transition-all border ${leagueId === league.id ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-100 hover:bg-slate-50 text-slate-900'}`}
                                >
                                    <div className="w-10 h-10 rounded-xl bg-white p-1.5 flex items-center justify-center shrink-0 border border-slate-100 shadow-sm">
                                        <ImageWithFallback src={league.id === '1' ? "/assets/fiwc.png" : (getLeagueLogo(league.id) || league.logo_url)} alt="" className="w-full h-full object-contain" />
                                    </div>
                                    <span className="font-black text-xs uppercase tracking-tight truncate">
                                        {getLeagueName(parseInt(league.id), lang, league.name)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="px-6 py-5 bg-white border-b border-gray-50 space-y-4">
                    <div className="flex w-full bg-slate-50 p-1.5 rounded-[22px] gap-1 shadow-inner border border-slate-100">
                        {[
                            { id: 'table', label: showBracketOnly ? t(lang, 'bracket') : t(lang, 'standings') },
                            { id: 'scorers', label: t(lang, 'scorers') },
                            { id: 'assists', label: t(lang, 'assists') },
                            { id: 'injuries', label: t(lang, 'injuryList') }
                        ].map(tab => (
                            <button 
                                key={tab.id} 
                                onClick={() => setActiveTab(tab.id as TabType)}
                                className={`flex-1 py-3 rounded-[18px] text-[10px] font-black uppercase tracking-widest transition-all duration-500 ease-out ${
                                    activeTab === tab.id 
                                    ? 'bg-white text-slate-900 shadow-sm shadow-slate-200 scale-[1.02] z-10' 
                                    : 'text-slate-400 md:hover:text-slate-600'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'table' && isUclUel && !showBracketOnly && (
                        <div className="flex justify-center gap-2">
                            {[
                                { id: 'league', label: t(lang, 'leagueStage') },
                                { id: 'knockout', label: t(lang, 'knockoutStage') },
                                ...(leagueId === '1' ? [{ id: 'scenario', label: t(lang, 'scenariosTab') }] : [])
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setSubTab(tab.id as any)}
                                    className={`px-4 py-1.5 rounded-full text-[10px] font-black transition-all ${
                                        subTab === tab.id
                                        ? 'bg-slate-900 text-white shadow-md'
                                        : 'bg-gray-50 text-gray-400'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {activeTab === 'table' && leagueId === '1' && subTab === 'league' && groupNames.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto no-scrollbar">
                            <button
                                onClick={() => setSelectedGroup(null)}
                                className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${
                                    selectedGroup === null ? 'bg-slate-900 text-white' : 'bg-gray-50 text-gray-400'
                                }`}
                            >
                                ALL
                            </button>
                            {groupNames.map(g => (
                                <button
                                    key={g}
                                    onClick={() => setSelectedGroup(g)}
                                    className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${
                                        selectedGroup === g ? 'bg-rose-500 text-white shadow-sm' : 'bg-gray-50 text-gray-400'
                                    }`}
                                >
                                    {g.replace('Group ', '')}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div 
                    ref={injuryListRef}
                    onScroll={(e) => {
                        sessionStorage.setItem(`standings_scroll_${leagueId}_${activeTab}`, e.currentTarget.scrollTop.toString());
                    }}
                    className={`flex-1 overflow-y-auto bg-white custom-scrollbar pb-10 ${subTab === 'knockout' ? 'overflow-x-hidden' : ''}`}
                >
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full space-y-4 py-20">
                            <div className="w-10 h-10 border-4 border-gray-100 border-t-rose-500 rounded-full animate-spin"></div>
                            <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest animate-pulse">{t(lang, 'syncingIntel')}</span>
                        </div>
                    ) : (
                        <div className={`animate-in fade-in slide-in-from-bottom-2 duration-500 ${subTab === 'knockout' ? 'h-full' : ''}`}>
                            {activeTab === 'table' && (
                                <>
                                    {showBracketOnly || subTab === 'knockout' ? (
                                        <BracketView leagueId={leagueId} season={effectiveSeason} fixtures={fixtures} initialRound={initialKnockoutRound} />
                                    ) : subTab === 'scenario' && leagueId === '1' ? (
                                        <WorldCupScenarios
                                            standings={standings}
                                            fixtures={fixtures}
                                            selectedGroup={selectedGroup}
                                            lang={lang}
                                        />
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-12 gap-1 px-4 py-3 bg-gray-50/50 backdrop-blur-md text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center sticky top-0 z-10">
                                                <div className="col-span-1">#</div>
                                                <div className="col-span-5 text-left pl-2">{t(lang, 'club')}</div>
                                                <div className="col-span-1">{t(lang, 'played')}</div>
                                                <div className="col-span-1 text-gray-900">{t(lang, 'points')}</div>
                                                <div className="col-span-1">{t(lang, 'goalDiff')}</div>
                                                <div className="col-span-1">{t(lang, 'win')}</div>
                                                <div className="col-span-1">{t(lang, 'draw')}</div>
                                                <div className="col-span-1">{t(lang, 'loss')}</div>
                                            </div>
                                            {leagueId === '1' ? (
                                                (() => {
                                                    const groups = standings.reduce((acc, item) => {
                                                        const g = (item.group || 'Group').trim();
                                                        if (!acc[g]) acc[g] = [];
                                                        acc[g].push(item);
                                                        return acc;
                                                    }, {} as Record<string, StandingItem[]>);
                                                    const sortedEntries = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
                                                    const entries = selectedGroup
                                                        ? sortedEntries.filter(([g]) => g === selectedGroup)
                                                        : sortedEntries;
                                                    return entries.map(([groupName, items]) => (
                                                        <div key={groupName}>
                                                            {!selectedGroup && (
                                                                <div className="px-4 py-2 bg-slate-50 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-gray-100 sticky top-[37px] z-[5]">
                                                                    {groupName}
                                                                </div>
                                                            )}
                                                            {items.map((item, index) => (
                                                                <StandingRow
                                                                    key={`${groupName}-${item.team.id}`}
                                                                    item={item}
                                                                    index={index}
                                                                    lang={lang}
                                                                    leagueId={leagueId}
                                                                    shouldShowCupQualification={shouldShowCupQualification}
                                                                    getStatusColor={getStatusColor}
                                                                    getTeamName={getTeamName}
                                                                />
                                                            ))}
                                                        </div>
                                                    ));
                                                })()
                                            ) : (
                                                standings.map((item, index) => (
                                                    <StandingRow
                                                        key={item.team.id}
                                                        item={item}
                                                        index={index}
                                                        lang={lang}
                                                        leagueId={leagueId}
                                                        shouldShowCupQualification={shouldShowCupQualification}
                                                        getStatusColor={getStatusColor}
                                                        getTeamName={getTeamName}
                                                    />
                                                ))
                                            )}
                                        </>
                                    )}
                                </>
                            )}

                            {(activeTab === 'scorers' || activeTab === 'assists') && (
                                <div className="p-4 space-y-3">
                                    {(activeTab === 'scorers' ? topScorers : topAssists).map((item, idx) => (
                                        <StatItem
                                            key={idx}
                                            item={item}
                                            index={idx}
                                            activeTab={activeTab}
                                            lang={lang}
                                            handlePlayerClick={handlePlayerClick}
                                            getTeamName={getTeamName}
                                        />
                                    ))}
                                    {(activeTab === 'scorers' ? topScorers : topAssists).length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-32 text-gray-300 gap-3">
                                            <Minus size={32} strokeWidth={2.5} />
                                            <span className="text-[10px] font-black uppercase tracking-widest">{t(lang, 'noStatsSynced')}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'injuries' && (
                                <div className="space-y-3">
                                    {/* 팀별 필터 탭 (엠블럼만 표기) */}
                                    <div className="flex items-center gap-2 px-4 py-3 overflow-x-auto no-scrollbar border-b border-gray-50 sticky -top-[1px] bg-white z-10">
                                        <button
                                            onClick={() => {
                                                setSelectedInjuryTeamId(null);
                                                injuryListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                                            }}
                                            className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${
                                                selectedInjuryTeamId === null
                                                ? 'bg-slate-900 text-white'
                                                : 'bg-gray-50 text-gray-400'
                                            }`}
                                        >
                                            {t(lang, 'all')}
                                        </button>
                                        {Array.from(new Map(injuries.map(item => [item.team.id, item.team])).values()).map((team: any) => (
                                            <button
                                                key={team.id}
                                                onClick={() => {
                                                    setSelectedInjuryTeamId(team.id);
                                                    injuryListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                className={`shrink-0 w-10 h-10 p-1.5 rounded-xl border-2 transition-all flex items-center justify-center ${
                                                    selectedInjuryTeamId === team.id
                                                    ? 'border-slate-900 bg-slate-50 scale-110 shadow-sm'
                                                    : 'border-transparent bg-gray-50/50'
                                                }`}
                                            >
                                                <img src={team.logo} alt={team.name} className="w-full h-full object-contain" title={team.name} />
                                            </button>
                                        ))}
                                    </div>

                                    <div className="p-4 space-y-3">
                                        {injuries
                                            .filter(item => selectedInjuryTeamId === null || item.team.id === selectedInjuryTeamId)
                                            .map((item, idx) => (
                                            <InjuryItem
                                                key={idx}
                                                item={item}
                                                lang={lang}
                                                handlePlayerClick={handlePlayerClick}
                                            />
                                        ))}
                                        {injuries.length === 0 && (
                                            <div className="flex flex-col items-center justify-center py-32 text-gray-300 gap-3">
                                                <Minus size={32} strokeWidth={2.5} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">{t(lang, 'noStatsSynced')}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                
                {activeTab === 'table' && !isUclUel && (
                    <div className="px-6 py-4 bg-white border-t border-gray-100 flex gap-6 justify-center overflow-x-auto no-scrollbar">
                        {(() => {
                            const policy = getLeaguePolicy(parseInt(leagueId));
                            const legends = [];
                            
                            if (policy) {
                                if (shouldShowCupQualification) {
                                    if (policy.ucl.length > 0) legends.push({ color: 'bg-blue-500', label: leagueId === '292' ? t(lang, 'acle') : t(lang, 'ucl') });
                                    if (policy.uel.length > 0) legends.push({ color: 'bg-orange-400', label: leagueId === '292' ? t(lang, 'acl2') : t(lang, 'uel') });
                                    if (policy.uecl.length > 0) legends.push({ color: 'bg-emerald-400', label: t(lang, 'uecl') });
                                }
                                if (policy.rel_po.length > 0) legends.push({ color: 'bg-amber-500', label: t(lang, 'rel_po') });
                                if (policy.rel.length > 0) legends.push({ color: 'bg-red-500', label: t(lang, 'rel') });
                            }

                            return legends.map(l => (
                                <div key={l.label} className="flex items-center gap-1.5 shrink-0">
                                    <div className={`w-1.5 h-1.5 rounded-full ${l.color}`}></div>
                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{l.label}</span>
                                </div>
                            ));
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
};
